import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../lib/session";
import { listCompetitions, myCompetitionIds } from "../lib/api";
import { Empty, PageHead, Loading, Notice } from "../components/ui";
import Back from "../components/Back";

export default function Competitions() {
  const { society, profile, isOrganiser } = useSession();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  const [mine, setMine] = useState(null);
  /**
   * Players see their own competitions first — a society running four years
   * of these gets long. Organisers see everything, because half their job is
   * setting up competitions they aren't playing in, and those would be
   * invisible otherwise.
   *
   * Nothing is hidden: "Everything" is one tap away, and every competition
   * stays readable to every member.
   */
  const [scope, setScope] = useState(isOrganiser ? "all" : "mine");

  useEffect(() => {
    Promise.all([
      listCompetitions(society.id),
      profile ? myCompetitionIds(society.id, profile.id) : Promise.resolve(new Set()),
    ])
      .then(([list, ids]) => { setRows(list); setMine(ids); })
      .catch((e) => setError(e.message));
  }, [society.id, profile?.id]);

  if (!rows) return <Loading />;

  const inScope = (c) => scope === "all" || mine?.has(c.id);
  const shown = rows
    .filter(inScope)
    .filter((c) =>
      filter === "all" ? true
      : filter === "running" ? c.status !== "completed"
      : c.status === "completed");
  const mineCount = rows.filter((c) => mine?.has(c.id)).length;

  return (
    <>
      <Back to="/society">Society</Back>
      <PageHead title="Competitions"
                sub={scope === "mine" && mineCount !== rows.length
                  ? `${mineCount} of ${rows.length} in ${society.name}`
                  : `${rows.length} in ${society.name}`}>
        {isOrganiser && (
          <button className="btn btn-primary" onClick={() => nav("/competitions/new")}>
            New competition
          </button>
        )}
      </PageHead>

      <Notice kind="error">{error}</Notice>

      {rows.length > 0 && mineCount !== rows.length && (
        <div className="scope">
          {[["mine", `Mine · ${mineCount}`], ["all", `Everything · ${rows.length}`]].map(([k, label]) => (
            <button key={k} data-on={scope === k} onClick={() => setScope(k)}>{label}</button>
          ))}
        </div>
      )}

      {rows.length > 0 && (
        <div style={{ display: "flex", gap: ".3rem", marginBottom: ".9rem" }}>
          {["all", "running", "finished"].map((k) => (
            <button key={k} onClick={() => setFilter(k)}
                    style={{ padding: ".42rem .8rem", cursor: "pointer", fontSize: ".8125rem",
                      fontWeight: 600, borderRadius: "var(--r)",
                      border: `1px solid ${filter === k ? "var(--ink)" : "var(--line)"}`,
                      background: filter === k ? "var(--ink)" : "none",
                      color: filter === k ? "#fff" : "var(--dim)" }}>
              {k[0].toUpperCase() + k.slice(1)}
            </button>
          ))}
        </div>
      )}

      {shown.length === 0 && scope === "mine" && rows.length > 0 ? (
        <Empty title="You're not in any of these yet"
               action={
                 <button className="btn btn-ghost" onClick={() => setScope("all")}>
                   Show the society's {rows.length} competition{rows.length === 1 ? "" : "s"}
                 </button>
               }>
          Once an organiser enters you in a competition it'll appear here.
          Nothing is hidden in the meantime — everything the society has run
          is still there to look at.
        </Empty>
      ) : shown.length === 0 ? (
        <Empty title={rows.length === 0 ? "Nothing on yet" : "Nothing matching"}
               action={isOrganiser && rows.length === 0 && (
                 <button className="btn btn-primary" onClick={() => nav("/competitions/new")}>
                   Set one up
                 </button>
               )}>
          A competition holds one or more rounds. A normal society day is a
          competition with a single round.
        </Empty>
      ) : (
        <div className="list">
          {shown.map((c) => {
            const rounds = c.rounds ?? [];
            const played = rounds.filter((r) => r.status === "completed").length;
            return (
              <Link key={c.id} to={`/c/${c.id}/today`} className="list-row">
                <span style={{ minWidth: 0 }}>
                  <strong style={{ display: "block" }}>{c.name}</strong>
                  <small className="muted" style={{ fontSize: ".75rem" }}>
                    {rounds.length} round{rounds.length === 1 ? "" : "s"}
                    {rounds.length > 1 && c.aggregation === "best_n" &&
                      ` · best ${c.counting_rounds} count`}
                    {played > 0 && ` · ${played} played`}
                  </small>
                </span>
                <span className={`pill ${c.status === "completed" ? "pill-quiet" : "pill-live"}`}>
                  {c.status === "completed" ? "Finished" : c.status}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}
